import { $api, unwrap } from '~/lib/api-client';
import Axios, {
  type AxiosRequestConfig,
  type CancelToken,
  type ResponseType,
} from 'axios';
import get from 'lodash-es/get';
import { fromUnixTime, formatISO, differenceInSeconds } from 'date-fns';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import env from '../Assets/env.json';
import { safeJsonParse } from '~/lib/utils';

interface ILiftAccessToken {
  token: string;
  liftId: number;
}

interface SocketEventData {
  type: string;
  data: unknown;
  esNumber?: string;
  messageType?: string;
}

interface SocketDetails {
  socket?: Socket | null;
  accessToken: string;
  resubscriberTimer?: NodeJS.Timeout;
  listeningForModules: string[];
}

interface ILiftStatusSocketApiOptions {
  liftId: number;
  uri: string;
  responseType?: ResponseType;
  payload?: unknown;
  urlParameters?: Record<string, string>;
  cancelToken?: CancelToken;
}

class SocketClient {
  /**
   * The URL of the Socket IO server of the LS Classic platform
   *
   * @type {string}
   * @memberof SocketClient
   */
  serverUrl: string;

  /**
   * The URL of the Socket IO server of the LS Next platform
   *
   * @type {string}
   * @memberof SocketClient
   */
  nextServerUrl: string;

  sockets: {
    [liftId: number]: SocketDetails;
  };

  localAccess = localStorage.getItem('socket-access');
  lastAccessTokenAndLift = safeJsonParse<ILiftAccessToken>(this.localAccess);

  private socketEventEmitter?: (event: SocketEventData) => void;
  private tokenGenerationInProgress: Map<number, Promise<string>> = new Map();

  constructor(serverUrl: string, nextServerUrl: string) {
    this.sockets = {};
    this.serverUrl = serverUrl;
    this.nextServerUrl = nextServerUrl;
  }

  setSocketEventEmitter(emitter?: (event: SocketEventData) => void) {
    this.socketEventEmitter = emitter;
  }

  /**
   * Generic function for assembling the external id for a lift
   * @param liftId The lift for which we want the external id
   */
  getExternalIdForLift(liftId: number) {
    return `${env.external_id_prefix}-${liftId}`;
  }

  /**
   * This function will create a access token for the external services on the LS NG Portal API
   * @param liftId The lift id for which to get the access token
   */
  public async generateExternalIdAccessToken(liftId: number): Promise<string> {
    // Check if token generation is already in progress for this lift
    const existingPromise = this.tokenGenerationInProgress.get(liftId);
    if (existingPromise) {
      console.debug(
        '[SOCKET-CLIENT] Token generation already in progress for lift',
        liftId
      );
      return existingPromise;
    }

    // Create a new promise for token generation
    const tokenPromise = (async () => {
      try {
        const data = await unwrap(
          $api['/liftstatus/auth/sense-liftstatus'].POST({
            body: {
              identifier: this.getExternalIdForLift(liftId),
              // TODO: fix OpenAPI spec
              identifierType: 'ext' as never,
            },
          })
        );

        const accessToken = data.accessToken;

        // Update both localStorage and the instance variable
        const tokenData = {
          token: accessToken,
          liftId,
        };

        localStorage.setItem('socket-access', JSON.stringify(tokenData));
        this.lastAccessTokenAndLift = tokenData;

        return accessToken;
      } finally {
        // Clean up the in-progress promise
        this.tokenGenerationInProgress.delete(liftId);
      }
    })();

    // Store the promise to prevent duplicate requests
    this.tokenGenerationInProgress.set(liftId, tokenPromise);

    return tokenPromise;
  }

  private async getTokenDate(token: string): Promise<string | undefined> {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length < 2 || !tokenParts[1]) {
        throw new Error('Invalid token format');
      }
      const value = safeJsonParse<{ exp: number }>(atob(tokenParts[1]));
      if (!value) {
        throw new Error('Invalid token payload');
      }

      const expDate = formatISO(fromUnixTime(value.exp));

      return expDate;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  async generateTokenOrCheckValidity(liftId: number): Promise<string> {
    // Checking if the last created token was has an hour or less left,
    // if true, we will create a new token with a new date stamp.
    // we do this because the tokens are only valid for 24 hours.
    let token;

    console.debug('[SOCKET-CLIENT] Checking token validity', { liftId });

    if (this.lastAccessTokenAndLift?.liftId === liftId) {
      const expDate = await this.getTokenDate(
        this.lastAccessTokenAndLift.token
      );

      // If we couldn't parse the token expiration date, generate a new token
      if (!expDate) {
        console.debug(
          '[SOCKET-CLIENT] Could not parse token expiration, generating new token'
        );
        token = await this.generateExternalIdAccessToken(liftId);
        return token;
      }

      const now = new Date();
      const expirationDate = new Date(expDate);

      // Check if the expiration date is valid
      if (isNaN(expirationDate.getTime())) {
        console.log(
          '[SOCKET-CLIENT] Invalid expiration date, generating new token'
        );
        token = await this.generateExternalIdAccessToken(liftId);
        return token;
      }

      const secondsDifference = differenceInSeconds(expirationDate, now);

      if (secondsDifference < 30) {
        token = await this.generateExternalIdAccessToken(liftId);
      } else {
        token = this.lastAccessTokenAndLift.token;
      }

      return token;
    } else {
      token = await this.generateExternalIdAccessToken(liftId);
      return token;
    }
  }

  /**
   * This function will connect a websocket for the specific lift if there isn't one yet. Resolves on socket connection
   * @param liftId The lift which you want to connect the websocket for
   * @param accessToken An optional already generated accessToken. If not provided one will be generated for you
   */
  public connect(liftId: number, accessToken?: string) {
    console.debug('[SOCKET-CLIENT] Connecting to socket', { liftId });

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      accessToken = (await this.generateTokenOrCheckValidity(liftId)) as string;

      // we already have a socket for the lift
      //
      if (this.sockets[liftId]) {
        resolve(this.sockets[liftId].socket);
      }

      // If there is still no access token we can't continue
      //
      if (!accessToken) {
        throw Error('No access token to communicate with external Api');
      }

      // if no socket for this lift exists create an object with the access token on it
      // the socket itself will be assigned later
      //
      if (!this.sockets[liftId]) {
        this.sockets[liftId] = {
          accessToken,
          listeningForModules: [],
        };
      }

      // lets assign the details to a variable so we don't have to keep accessing this.sockets
      //
      const socketDetails = this.sockets[liftId];

      socketDetails.accessToken = accessToken;

      this.lastAccessTokenAndLift = {
        liftId,
        token: accessToken,
      };

      // if no socket has been created yet for this lift id but we do have an access token (this should always be the case)
      // create a socket
      //
      if (!socketDetails.socket && socketDetails.accessToken) {
        this.setSocketOnSocketDetails(socketDetails);
        // if we already have a socket but our accessToken changed close the current socket for this lift and update the access token
        // then create a new socket with the new access token
        //
      } else if (
        socketDetails.socket &&
        accessToken &&
        socketDetails.accessToken !== accessToken
      ) {
        socketDetails.socket.close();

        socketDetails.accessToken = accessToken;
        this.setSocketOnSocketDetails(socketDetails);
      }

      // if for some reason we don't have a socket here throw an error as it will anyway later but this way
      // we control the error message
      //
      if (!socketDetails.socket) {
        reject('No socket available');
      }

      // from here start listening on the different events and act accordingly
      //
      socketDetails?.socket?.on('connect', () => {
        console.debug('[SOCKET-CLIENT] socket connected');
        // Clear any existing timer before setting a new one
        if (socketDetails.resubscriberTimer) {
          clearInterval(socketDetails.resubscriberTimer);
        }

        // Don't recreate the socket connection - it's already connected
        // Instead, we might want to re-subscribe to channels if needed
        // socketDetails.resubscriberTimer = setInterval(() => {
        //   // Re-subscribe to channels here if needed
        //   console.debug('[SOCKET-CLIENT] Timer tick - socket still connected');
        // }, 60 * 1000);

        resolve(socketDetails.socket);
      });

      socketDetails?.socket?.on('connect_error', async (e) => {
        console.debug('[SOCKET-CLIENT] Socket error', e);
        this.socketEventEmitter?.({
          type: 'socket.error',
          data: e,
        });

        // Remove and reset a token in case of a corrupt token
        localStorage.removeItem('socket-access');
        this.lastAccessTokenAndLift = null;
        reject(e);
      });

      socketDetails?.socket?.on('disconnect', (e) => {
        console.debug('[SOCKET-CLIENT] Socket disconnect', e);
        this.socketEventEmitter?.({
          type: 'socket.disconnect',
          data: e,
        });
        if (socketDetails.resubscriberTimer) {
          clearInterval(socketDetails.resubscriberTimer);
          socketDetails.resubscriberTimer = undefined;
        }
        socketDetails.socket = null;
        reject(e);
      });

      socketDetails?.socket?.on('module', (data) => {
        // console.debug('[SOCKET-CLIENT] Module data', data);
        const esNumber = data.es;
        const messageType = data.message.f;
        this.socketEventEmitter?.({
          type: 'module',
          data,
          esNumber,
          messageType,
        });
      });

      socketDetails?.socket?.on('provisioning', (data) => {
        // console.log( "[SOCKET-CLIENT] Provisioning", data );
        const esNumber = data.es;
        const messageType = data.message.e;
        this.socketEventEmitter?.({
          type: 'provisioning',
          data,
          esNumber,
          messageType,
        });
      });
    });
  }

  /**
   * Private function to create the connection to the websocket.
   * @param socketDetails The details of the socket that we defined earlier (including accesstoken)
   */
  private setSocketOnSocketDetails(socketDetails: SocketDetails) {
    // If there's an existing socket, close it first
    if (socketDetails.socket) {
      console.log(
        '[SOCKET-CLIENT] Closing existing socket before creating new one'
      );
      socketDetails.socket.disconnect();
      socketDetails.socket = null;
    }

    const connectURL = `${this.serverUrl}/?access_token=${socketDetails.accessToken}`;

    socketDetails.socket = io(connectURL, {
      path: '/liftstatus/socket.io',
      reconnectionAttempts: 10,
      transports: ['websocket'],
    });
  }

  /**
   * Generic function to retrieve the socketDetails for a lift
   * @param liftId The lift id of which you want the details
   */
  private getSocketDetails(liftId: number) {
    const socketDetails = this.sockets[liftId];

    if (!socketDetails) {
      throw new Error('Socket not created');
    }

    if (!socketDetails.socket) {
      throw new Error('Socket not connected');
    }

    return socketDetails;
  }

  /**
   * Retrieves the external id access token for the lift
   * @param liftId
   */
  public getAccessToken(liftId: number) {
    const socketDetails = this.getSocketDetails(liftId);
    return socketDetails?.accessToken;
  }
  /**
   * Function to send something over a socket connection. probably will be rarely called directly
   * @param liftId The lift id for which you want to call the websocket
   * @param eventName The name of the event
   * @param data The data for the event
   */
  public send(liftId: number, eventName: string, data: unknown) {
    return new Promise((resolve, reject) => {
      try {
        const socketDetails = this.getSocketDetails(liftId);

        const socket = socketDetails.socket;

        if (!socket) {
          throw new Error('Socket not available');
        }

        socket.emit(eventName, data, (error: Error | null, data: unknown) => {
          if (error) {
            reject(error);
          } else if (get(data, 'error')) {
            reject(get(data, 'error'));
          } else {
            resolve(data);
          }
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  ///////////////////////////////////////////////////////////
  // Gate Module related calls
  ///////////////////////////////////////////////////////////
  //
  public listenModule(liftId: number, esNumber: string) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const socketDetails = this.getSocketDetails(liftId);

        // Check if already subscribed
        //
        if (socketDetails.listeningForModules.includes(esNumber)) {
          resolve('Already listening');
        } else {
          const sendResponse = await this.send(liftId, 'listen', {
            es_number: esNumber,
          });
          socketDetails.listeningForModules.push(esNumber);
          resolve(sendResponse);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  unlistenModule(
    liftId: number,
    esNumber: string,
    forceUnlisten: boolean = false
  ) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const socketDetails = this.getSocketDetails(liftId);

        // Check if subscribed
        //
        if (
          !socketDetails.listeningForModules.includes(esNumber) &&
          !forceUnlisten
        ) {
          resolve(undefined);
        } else {
          const sendResponse = await this.send(liftId, 'unlisten', {
            es_number: esNumber,
          });
          socketDetails.listeningForModules.splice(
            socketDetails.listeningForModules.indexOf(esNumber),
            1
          );
          resolve(sendResponse);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  getSensorData(liftId: number, esNumber: string) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const sendResponse = await this.send(liftId, 'getSensorData', {
          es_number: esNumber,
        });
        resolve(sendResponse);
      } catch (e) {
        reject(e);
      }
    });
  }

  getModuleStatus(liftId: number, esNumber: string) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const sendResponse = await this.send(liftId, 'getModuleStatus', {
          es_number: esNumber,
        });
        resolve(sendResponse);
      } catch (e) {
        console.error(
          '[SOCKET-CLIENT] Error getting module status',
          e,
          liftId,
          esNumber
        );
        reject(e);
      }
    });
  }

  //////////////////////////////////

  public connectToNextSocket = () => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const token = window.localStorage.getItem('liftstatus-user-token');

      const socket = io(this.nextServerUrl, {
        transports: ['websocket'],
        path: '/liftstatus/socket.io',
        reconnectionAttempts: 5,
        // The browser sends the session cookie on the handshake; the query
        // parameter stays for an API that does not read cookies yet
        withCredentials: true,
        query: token ? { access_token: token } : undefined,
      });

      if (socket) {
        if (!socket.connected) {
          socket.on('connect', () => {
            resolve(socket);
          });
        } else {
          resolve(socket);
        }

        socket.on('disconnect', () => {
          reject('disconnected');
        });
      }
    });
  };

  ///////////////////////////////////////////////////////////
  // LS NG Portal API related calls
  ///////////////////////////////////////////////////////////
  //
  /**
   * Headers are always the same for all calls
   */
  getHeaders(accessToken: string) {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
  }

  /**
   * Generic Axios post method wrapper for the external Sense API
   */
  async post(options: ILiftStatusSocketApiOptions): Promise<unknown> {
    console.debug('[SOCKET-CLIENT] POST', { options });

    // First try with the last used access token
    const accessToken = await this.generateTokenOrCheckValidity(options.liftId);

    if (this.lastAccessTokenAndLift) {
      this.lastAccessTokenAndLift.liftId = options?.liftId;
      this.lastAccessTokenAndLift.token = accessToken as string;
    }

    // If there is still no access token we can't continue
    //
    if (!accessToken) {
      throw Error('No access token to communicate with external Api');
    }

    const config: AxiosRequestConfig = {
      params: options.urlParameters
        ? new URLSearchParams(options.urlParameters)
        : undefined,
      headers: this.getHeaders(accessToken as string),
      ...(options.cancelToken && { cancelToken: options.cancelToken }),
    };

    if (options.responseType) {
      config.responseType = options.responseType;
    }

    try {
      const response = await Axios.post(
        `${this.serverUrl}${options.uri}`,
        options.payload,
        config
      );

      return response;
    } catch (error: unknown) {
      if (Axios.isCancel(error)) {
        // We dont throw here because we dont want the consumer to get an error
        // and display that to the user since we only get to this
        // statement if the user has canceled a previous running request
        // to trigger a new request.
        // we're logging for our own purposes
        console.error('Request canceled', (error as Error)?.message);
        return undefined; // Return early for canceled requests
      } else {
        const axiosError = error as {
          response?: { data: unknown };
          request?: unknown;
          message?: string;
        };
        if (axiosError.response) {
          // Request made and server responded
          throw axiosError.response.data;
        } else if (axiosError.request) {
          // The request was made but no response was received
          throw new Error('Network request failed');
        } else {
          // Something happened in setting up the request that triggered an Error
          throw new Error(axiosError.message || 'Request setup failed');
        }
      }
    }
  }

  async get(options: ILiftStatusSocketApiOptions): Promise<unknown> {
    console.debug('[SOCKET-CLIENT] GET', { options });

    const accessToken = (await this.generateTokenOrCheckValidity(
      options.liftId
    )) as string;

    // Check if there is a list used access token
    //

    if (!accessToken) {
      throw Error('No access token to communicate with external Api');
    }

    this.lastAccessTokenAndLift = {
      token: accessToken,
      liftId: options.liftId,
    };

    const config: AxiosRequestConfig = {
      params: options.urlParameters
        ? new URLSearchParams(options.urlParameters)
        : undefined,
      headers: this.getHeaders(accessToken as string),
    };

    if (options.responseType) {
      config.responseType = options.responseType;
    }
    return Axios.get(`${this.serverUrl}${options.uri}`, config).catch(
      (error) => error
    );
  }
}

export const socketClient = new SocketClient(env.socket.endpoint, env.endpoint);
